import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, QrCode, Wallet } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api, { formatCurrency } from '../lib/api';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    cpf: user?.cpf || '',
    whatsapp: user?.whatsapp || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    zip_code: user?.zip_code || '',
    observations: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.cpf || !formData.whatsapp || 
        !formData.address || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      
      const orderData = {
        items: cart.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_price: item.product_price,
          quantity: item.quantity,
          subtotal: item.product_price * item.quantity,
        })),
        total: cart.total,
        customer: {
          name: formData.name,
          email: formData.email,
          cpf: formData.cpf.replace(/\D/g, ''),
          whatsapp: formData.whatsapp.replace(/\D/g, ''),
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code.replace(/\D/g, ''),
          observations: formData.observations,
        },
        payment_method: paymentMethod,
      };

      const response = await api.post('/orders', orderData);
      
      toast.success('Pedido criado com sucesso!');
      navigate(`/pedido/${response.data.id}`);
      
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    navigate('/carrinho');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <h1 className="font-heading text-2xl font-bold text-foreground mb-6">
          Finalizar Compra
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Customer Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Data */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-foreground mb-4">
                  Dados Pessoais
                </h2>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Completo *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="rounded-xl"
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="rounded-xl"
                      required
                      data-testid="checkout-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cpf">CPF *</Label>
                    <Input
                      id="cpf"
                      name="cpf"
                      value={formData.cpf}
                      onChange={(e) => setFormData(prev => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                      className="rounded-xl"
                      maxLength={14}
                      required
                      data-testid="checkout-cpf"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp *</Label>
                    <Input
                      id="whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: formatPhone(e.target.value) }))}
                      className="rounded-xl"
                      maxLength={15}
                      required
                      data-testid="checkout-whatsapp"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-foreground mb-4">
                  Endereço de Entrega
                </h2>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="address">Endereço Completo *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Rua, número, complemento"
                      className="rounded-xl"
                      required
                      data-testid="checkout-address"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="city">Cidade *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="rounded-xl"
                      required
                      data-testid="checkout-city"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state">Estado *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      maxLength={2}
                      placeholder="SP"
                      className="rounded-xl"
                      required
                      data-testid="checkout-state"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">CEP *</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, zip_code: formatCEP(e.target.value) }))}
                      className="rounded-xl"
                      maxLength={9}
                      required
                      data-testid="checkout-cep"
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="observations">Observações</Label>
                  <Textarea
                    id="observations"
                    name="observations"
                    value={formData.observations}
                    onChange={handleChange}
                    placeholder="Instruções para entrega, ponto de referência, etc."
                    className="rounded-xl"
                    data-testid="checkout-observations"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-foreground mb-4">
                  Forma de Pagamento
                </h2>
                
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  className="space-y-3"
                >
                  <label
                    htmlFor="pix"
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      paymentMethod === 'pix' ? 'border-primary bg-primary/5' : 'border-slate-200'
                    }`}
                  >
                    <RadioGroupItem value="pix" id="pix" data-testid="payment-pix" />
                    <QrCode className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium">PIX</p>
                      <p className="text-sm text-muted-foreground">Pagamento instantâneo via QR Code</p>
                    </div>
                    <span className="ml-auto bg-success/10 text-success text-xs font-medium px-2 py-1 rounded-full">
                      Recomendado
                    </span>
                  </label>
                  
                  <label
                    htmlFor="credit_card"
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      paymentMethod === 'credit_card' ? 'border-primary bg-primary/5' : 'border-slate-200'
                    }`}
                  >
                    <RadioGroupItem value="credit_card" id="credit_card" data-testid="payment-credit" />
                    <CreditCard className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium">Cartão de Crédito</p>
                      <p className="text-sm text-muted-foreground">Parcele em até 12x</p>
                    </div>
                  </label>
                  
                  <label
                    htmlFor="debit_card"
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      paymentMethod === 'debit_card' ? 'border-primary bg-primary/5' : 'border-slate-200'
                    }`}
                  >
                    <RadioGroupItem value="debit_card" id="debit_card" data-testid="payment-debit" />
                    <Wallet className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium">Cartão de Débito</p>
                      <p className="text-sm text-muted-foreground">Débito à vista</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 className="font-heading text-lg font-bold text-foreground mb-4">
                  Resumo do Pedido
                </h2>
                
                {/* Items */}
                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        <img
                          src={item.product_image || 'https://images.unsplash.com/photo-1612708074992-c7936e82c99b?w=100'}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">Qtd: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-medium">
                        {formatCurrency(item.product_price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(cart.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Frete</span>
                    <span className="text-success">A combinar</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total</span>
                    <span className="text-primary" data-testid="checkout-total">
                      {formatCurrency(cart.total)}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full mt-6 rounded-xl shadow-primary"
                  disabled={loading}
                  data-testid="confirm-order-button"
                >
                  {loading ? 'Processando...' : 'Confirmar Pedido'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CheckoutPage;
